import { EUserRole } from '__generated__/globalTypes';
import { LogoHeader } from 'components/site/layouts/DefaultPageLayout/LogoHeader';
import { MenuHeader } from 'components/site/layouts/DefaultPageLayout/MenuHeader';
import { WebButton } from 'elements/button/WebButton';
import { useLinkMapAndGuideToUser } from 'hooks/ownership/useLinkMapAndGuideToUser';
import { EColors, EFonts, EGrads, EShadows } from 'libs/styles/styling';
import { S_PageStyle } from 'pages/page/PageStyle';
import { useContext } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { GlobalContext } from 'session/state/context';
import { setLoggedUser } from 'session/state/user/userActions';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
    title?: string;
    children?: JSX.Element;
    forceLogin: boolean;
}

export const DefaultPageLayout = (props: Props): JSX.Element => {
    const globalContext = useContext(GlobalContext);
    const user = globalContext.state.loggedUser;
    const navigate = useNavigate();
    const location = useLocation();

    function logout() {
        globalContext.dispatch(setLoggedUser(null));
    }

    function redirectToLogin() {
        navigate('/admin');
    }

    useLinkMapAndGuideToUser();

    const isLoginPage = location.pathname === '/';

    if (globalContext.state.loggedUserChecked && !user && props.forceLogin) {
        navigate('/login');
    }

    return (
        <S_DefaultPageLayout className={props.className}>
            <S_Inner>
                <S_Header>
                    <LogoHeader />
                    {user && (
                        <S_Right>
                            <S_User>{user.userId}</S_User>
                            <WebButton
                                text={'Logout'}
                                type="primary"
                                shadow={EShadows.STRONG}
                                onClick={logout}
                            />
                        </S_Right>
                    )}
                    {/* Do not show login button on login page */}
                    {!user && !isLoginPage && (
                        <S_Right>
                            <WebButton
                                text={'Login'}
                                type="primary"
                                shadow={EShadows.STRONG}
                                onClick={redirectToLogin}
                            />
                        </S_Right>
                    )}
                </S_Header>
                {user && (user.role === EUserRole.ADMIN || user.role === EUserRole.RESELLER) && (
                    <MenuHeader />
                )}
                {globalContext.state.loggedUserChecked && (user || !props.forceLogin) && (
                    <>
                        <S_H1>{props.title}</S_H1>
                        {props.children}
                    </>
                )}
            </S_Inner>
        </S_DefaultPageLayout>
    );
};

const S_DefaultPageLayout = styled(S_PageStyle)`
    width: 100%;
    height: 100%;
    margin: 0 auto;
    background: ${EGrads.PRIM_GRAD};
    position: relative;
    overflow: hidden;
    min-height: unset;
    @media only screen and (max-width: 1259px) {
        padding: 0 30px;
    }
`;

const S_Inner = styled.div`
    display: flex;
    flex-direction: column;
    margin: auto;
    padding: 30px 0;
    position: relative;
    max-width: 1260px;
    width: 100%;
    height: 100%;
`;

const S_Header = styled.div`
    display: flex;
    margin: 20px 0 0 0;
    justify-content: space-between;
`;

const S_Right = styled.div`
    display: flex;
    align-items: center;
`;

const S_User = styled.div`
    font-family: ${EFonts.Medium};
    font-size: 18px;
    color: ${EColors.WHITE};
    margin: 0 20px 0 0;
`;

const S_H1 = styled.h1`
    margin: 30px 0px 40px;
    color: ${EColors.WHITE};
    font-family: ${EFonts.Bold};
    font-size: 42px;
    line-height: 50px;
    letter-spacing: -0.03em;
`;
